import React, { useRef, useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Map, AdvancedMarker, useMap, useMapsLibrary, ControlPosition } from "@vis.gl/react-google-maps";
import { Button } from "../../../../shadcn/ui/button";
import { MapPin, CheckCircle2, AlertCircle, Navigation } from "lucide-react";
import { getCachedLocation, requestUserLocation } from "../../../../utils/locationUtils";
import {
  getUserCountryCoordinates,
  getCountryFromTimezone,
  getCoordinatesForCountry,
} from "../../../../utils/countryCoordinatesMap";
import MapErrorBoundary from "../../../../components/MapErrorBoundary";
import logger from "../../../../utils/logger";
import { parseGooglePlace } from "./GooglePlacesSearchInput";
import { getTimezoneForCountry } from "../../../../utils/timezoneUtils";

const extractCountryShort = addressComponents => {
  if (!Array.isArray(addressComponents)) return "";
  const country = addressComponents.find(c => Array.isArray(c.types) && c.types.includes("country"));
  return country?.short_name || "";
};

const extractNameFromComponents = addressComponents => {
  if (!Array.isArray(addressComponents)) return "";
  const priority = ["premise", "subpremise", "neighborhood", "sublocality_level_1", "sublocality", "route", "locality"];
  for (const type of priority) {
    const comp = addressComponents.find(c => Array.isArray(c.types) && c.types.includes(type));
    if (comp) return comp.long_name;
  }
  return "";
};

const isValidCoords = (lat, lng) => isFinite(Number(lat)) && isFinite(Number(lng));

const MapPanEffect = ({ target }) => {
  const map = useMap();
  useEffect(() => {
    if (!map || !target) return;
    const lat = Number(target.latitude);
    const lng = Number(target.longitude);
    if (!isValidCoords(lat, lng)) return;
    map.panTo({ lat, lng });
    const currentZoom = map.getZoom() || 0;
    const targetZoom = target.zoom || 17;
    if (currentZoom < targetZoom) {
      map.setZoom(targetZoom);
    }
  }, [map, target]);
  return null;
};

const InstanceLocationPicker = ({ initialValue, onConfirm, onCancel }) => {
  const hasInitialCoords =
    initialValue?.latitude != null &&
    initialValue?.longitude != null &&
    isValidCoords(initialValue.latitude, initialValue.longitude);

  const [selected, setSelected] = useState(initialValue || null);
  const [panTarget, setPanTarget] = useState(
    hasInitialCoords
      ? {
          latitude: Number(initialValue.latitude),
          longitude: Number(initialValue.longitude),
          zoom: 17,
        }
      : null
  );
  const [mapError, setMapError] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const isMounted = useRef(true);

  // Synchronous initial center: initialValue → cached location → timezone-based country
  const [mapCenter] = useState(() => {
    if (initialValue?.latitude != null && initialValue?.longitude != null) {
      const lat = Number(initialValue.latitude);
      const lng = Number(initialValue.longitude);
      if (isFinite(lat) && isFinite(lng)) return { lat, lng };
    }
    const cached = getCachedLocation();
    if (cached?.latitude != null && cached?.longitude != null) {
      const lat = Number(cached.latitude);
      const lng = Number(cached.longitude);
      if (isFinite(lat) && isFinite(lng)) return { lat, lng };
    }
    const code = getCountryFromTimezone();
    if (code) {
      const coords = getCoordinatesForCountry(code);
      if (coords) return { lat: coords.latitude, lng: coords.longitude };
    }
    return null;
  });

  const placesLib = useMapsLibrary("places");
  const geocodingLib = useMapsLibrary("geocoding");

  // Async fallback: IP geolocation to pan to user's country if no sync center found
  useEffect(() => {
    if (mapCenter) return;
    (async () => {
      const coords = await getUserCountryCoordinates();
      if (coords && isMounted.current) {
        const lat = Number(coords.latitude);
        const lng = Number(coords.longitude);
        if (isFinite(lat) && isFinite(lng)) {
          setPanTarget({ lat, lng });
        }
      }
    })();
  }, []);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      if (autocompleteRef.current && window.google?.maps?.event) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, []);

  // Set up Places Autocomplete on the input
  useEffect(() => {
    if (!placesLib || !inputRef.current) return;

    const autocomplete = new placesLib.Autocomplete(inputRef.current, {
      fields: ["geometry", "name", "formatted_address", "place_id", "address_components"],
      strictBounds: false,
      types: ["establishment", "geocode"],
    });
    autocompleteRef.current = autocomplete;

    const listener = autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();
      if (place?.geometry?.location) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        const parsed = parseGooglePlace(place);
        const newSelected = {
          ...parsed,
          latitude: lat,
          longitude: lng,
          address: place.formatted_address || place.name || parsed.streetAddress,
          name: place.name || parsed.buildingName || "",
          placeId: place.place_id || "",
          countryCode: parsed.countryCode || extractCountryShort(place.address_components),
        };
        if (isMounted.current) {
          setSelected(newSelected);
          setPanTarget(newSelected);
        }
      }
    });

    return () => {
      window.google?.maps?.event?.removeListener(listener);
      window.google?.maps?.event?.clearInstanceListeners(autocomplete);
    };
  }, [placesLib]);

  // Map click → reverse geocode
  const handleMapClick = useCallback(
    event => {
      const lat = event.detail.latLng?.lat;
      const lng = event.detail.latLng?.lng;
      if (lat == null || lng == null) return;

      setPanTarget({ latitude: lat, longitude: lng, zoom: 17, timestamp: Date.now() });

      if (!geocodingLib) {
        if (isMounted.current) setSelected({ latitude: lat, longitude: lng });
        return;
      }

      const geocoder = new geocodingLib.Geocoder();
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (!isMounted.current) return;
        if (status === "OK" && results[0]) {
          const parsed = parseGooglePlace(results[0]);
          const name = extractNameFromComponents(results[0].address_components) || parsed.buildingName;
          setSelected({
            ...parsed,
            latitude: lat,
            longitude: lng,
            address: results[0].formatted_address || parsed.streetAddress,
            name: name || parsed.buildingName || "Pinned Location",
            placeId: results[0].place_id || "",
            countryCode: parsed.countryCode || extractCountryShort(results[0].address_components),
          });
        } else {
          setSelected({ latitude: lat, longitude: lng });
        }
      });
    },
    [geocodingLib]
  );

  const handleUseMyLocation = useCallback(async () => {
    if (gettingLocation) return;
    setGettingLocation(true);
    try {
      const location = await requestUserLocation();
      if (!location || !isMounted.current) {
        setGettingLocation(false);
        return;
      }

      const { latitude, longitude } = location;

      if (geocodingLib) {
        const geocoder = new geocodingLib.Geocoder();
        geocoder.geocode({ location: { lat: latitude, lng: longitude } }, (results, status) => {
          if (!isMounted.current) return;
          if (status === "OK" && results[0]) {
            const parsed = parseGooglePlace(results[0]);
            const name = extractNameFromComponents(results[0].address_components) || parsed.buildingName;
            setSelected({
              ...parsed,
              latitude,
              longitude,
              address: results[0].formatted_address || parsed.streetAddress,
              name: name || parsed.buildingName || "Your Location",
              placeId: results[0].place_id || "",
              countryCode: parsed.countryCode || extractCountryShort(results[0].address_components),
            });
          } else {
            setSelected({ latitude, longitude, address: "Your location" });
          }
          setPanTarget({ latitude, longitude });
          setGettingLocation(false);
        });
      } else {
        const detectedCountryCode = location.countryCode || "";
        const detectedTz = detectedCountryCode ? getTimezoneForCountry(detectedCountryCode) : null;
        setSelected({
          latitude,
          longitude,
          address: location.city ? [location.city, location.country].filter(Boolean).join(", ") : "Your location",
          name: location.city || "Your Location",
          city: location.city || "",
          country: location.country || "",
          countryCode: detectedCountryCode,
          timezone: detectedTz || "",
          placeId: "",
        });
        setPanTarget({ latitude, longitude });
        setGettingLocation(false);
      }
    } catch (err) {
      logger.error("Error getting location:", err);
      if (isMounted.current) setGettingLocation(false);
    }
  }, [geocodingLib, gettingLocation]);

  return (
    <div className="h-full flex flex-col">
      <style>{`
        .pac-container {
          z-index: 99999 !important;
          pointer-events: auto !important;
          border-radius: 8px !important;
          box-shadow: 0 4px 24px rgba(0,0,0,0.13) !important;
          border: 1px solid #e5e7eb !important;
          margin-top: 4px !important;
          font-family: inherit !important;
        }
        .pac-item {
          padding: 8px 12px !important;
          cursor: pointer !important;
          font-size: 13px !important;
          line-height: 1.5 !important;
        }
        .pac-item:hover { background-color: #eff6ff !important; }
        .pac-item-selected { background-color: #dbeafe !important; }
        .pac-matched { font-weight: 600 !important; color: #1d4ed8 !important; }
      `}</style>

      {/* Map Container */}
      <div className="relative overflow-hidden w-full flex-1 min-h-[300px] rounded-2xl border border-gray-200 shadow-xl bg-gray-50">
        {!mapError ? (
          <MapErrorBoundary onError={() => setMapError(true)}>
            <Map
              mapId="DEMO_MAP_ID"
              defaultCenter={mapCenter || { lat: 0, lng: 0 }}
              defaultZoom={hasInitialCoords ? 17 : mapCenter ? 14 : 4}
              zoomControl={true}
              zoomControlOptions={{
                position: ControlPosition.RIGHT_CENTER,
              }}
              fullscreenControl={false}
              disableDefaultUI={true}
              onClick={handleMapClick}
              style={{ width: "100%", height: "100%" }}
            >
              {panTarget && <MapPanEffect target={panTarget} />}

              {selected?.latitude != null && selected?.longitude != null && (
                <AdvancedMarker position={{ lat: selected.latitude, lng: selected.longitude }}>
                  <div className="relative">
                    <div className="absolute -translate-x-1/2 -translate-y-full">
                      <MapPin className="w-8 h-8 text-red-600 fill-red-100 drop-shadow-lg" />
                    </div>
                  </div>
                </AdvancedMarker>
              )}
            </Map>
          </MapErrorBoundary>
        ) : (
          <div className="absolute inset-0 bg-gray-50 flex items-center justify-center p-6 text-center">
            <div className="max-w-xs">
              <div className="w-12 h-12 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-6 h-6 text-amber-600" />
              </div>
              <h3 className="text-base font-bold text-gray-900 mb-1">Map Loading Failed</h3>
              <p className="text-sm text-gray-500 mb-4">We couldn't initialize Google Maps. Please try again.</p>
              <button
                type="button"
                onClick={() => setMapError(false)}
                className="text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
              >
                Try reloading map
              </button>
            </div>
          </div>
        )}

        {/* Search Input - absolute over map */}
        <div className="absolute top-4 inset-x-4 z-20 pointer-events-none">
          <div className="max-w-md mx-auto">
            <div className="relative pointer-events-auto">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                <MapPin className="w-4 h-4 text-blue-500" />
              </div>
              <input
                ref={inputRef}
                type="text"
                placeholder="Search for a location or address"
                className="w-full pl-10 pr-4 py-3 text-sm border border-blue-200 rounded-xl bg-white placeholder:text-gray-400 text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-400 transition-all shadow-2xl"
              />
            </div>
          </div>
        </div>

        {/* Use my location button */}
        <div className={`absolute z-30 ${selected ? "bottom-24 left-4" : "bottom-4 left-4"}`}>
          <button
            type="button"
            onClick={handleUseMyLocation}
            disabled={gettingLocation}
            className="flex items-center gap-2 px-3 py-2 bg-white/95 backdrop-blur-sm rounded-xl shadow-lg border border-gray-100 hover:bg-white hover:shadow-xl transition-all text-sm font-medium text-gray-700"
          >
            {gettingLocation ? (
              <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            ) : (
              <Navigation className="w-4 h-4 text-blue-600" />
            )}
            Use my location
          </button>
        </div>

        {/* Hint when no location selected */}
        {!selected && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 pointer-events-none whitespace-nowrap">
            <div className="bg-white/90 backdrop-blur-sm px-4 py-2 rounded-xl shadow-lg border border-gray-100 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium text-gray-700">Click on the map to pin your location</span>
            </div>
          </div>
        )}

        {/* Selected location banner */}
        {selected && (
          <div className="absolute bottom-4 left-4 right-16 sm:right-auto sm:max-w-md z-20">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white/95 backdrop-blur-sm p-4 rounded-xl shadow-xl border border-green-100 flex items-center gap-3"
            >
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <CheckCircle2 className="w-6 h-6 text-green-600" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-gray-900 truncate">
                  {selected.name || selected.buildingName || "Location Selected"}
                </p>
                <p className="text-xs text-gray-600 truncate">
                  {selected.streetAddress || selected.address || "Address detected"}
                </p>
                {(selected.city || selected.country) && (
                  <p className="text-[11px] text-gray-400 truncate">
                    {[selected.city, selected.region, selected.country].filter(Boolean).join(", ")}
                    {selected.timezone ? ` • ${selected.timezone}` : ""}
                  </p>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex gap-2 justify-end pt-3">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          type="button"
          variant="spaciadefault"
          onClick={() => {
            if (selected) onConfirm(selected);
          }}
          disabled={!selected}
        >
          Confirm Location
        </Button>
      </div>
    </div>
  );
};

export default InstanceLocationPicker;
